/* ═══════════════════════════════════════════════════════════════
   The Designer — the revision-styles punch-in panel.

   HOW post-issuance changes render, dictated by the architect: strike /
   bold / underline styles, Word's highlighter palette, print colors,
   change bars, and the master "show marks" switch. WHAT changed is
   always computed against the baseline — these settings never touch
   content. Stored per spec (migration 033 `revision_settings`), applied
   identically on the paper and in the .docx.

   Rebuilt clean from the Designer's kit patterns (portalled, fixed,
   Esc + mousedown-outside dismiss, ≥32px hit areas, tokens only for
   chrome). The swatches are Word CONTENT colors that print — mirroring
   the export palette exactly — not theme chrome.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Issuance, RevisionMarkStyle, RevisionSettings } from '../../shared/types';
import { markCssText } from './revisions';
import { ColorPickerPopover } from '../components/ui/color-picker';
import { appLayerProps } from '../components/ui/app-layer';

const PANEL_W = 316;

/** Word's highlighter palette — mirrors the export exactly. */
const HIGHLIGHTS: Array<{
  key: NonNullable<RevisionMarkStyle['highlight']>;
  css: string | null;
  label: string;
}> = [
  { key: 'none', css: null, label: 'No highlight' },
  { key: 'yellow', css: '#FFFF00', label: 'Yellow highlight' },
  { key: 'green', css: '#00FF00', label: 'Green highlight' },
  { key: 'cyan', css: '#00FFFF', label: 'Cyan highlight' },
  { key: 'magenta', css: '#FF00FF', label: 'Magenta highlight' },
  { key: 'gray', css: '#C0C0C0', label: 'Gray highlight' },
];

/** Word's standard font colors — content colors that print, not UI chrome. */
const COLORS: Array<{ key: string; css: string | null; label: string }> = [
  { key: '', css: null, label: 'Automatic (body color)' },
  { key: 'FF0000', css: '#FF0000', label: 'Red text' },
  { key: '0070C0', css: '#0070C0', label: 'Blue text' },
  { key: '00B050', css: '#00B050', label: 'Green text' },
];

function CheckRow({
  label,
  sub,
  checked,
  onToggle,
}: {
  label: string;
  sub?: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      className="dsg-rsp-check"
      onClick={onToggle}
    >
      <span className={`dsg-rsp-box${checked ? ' is-on' : ''}`} aria-hidden="true">
        {checked && (
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        )}
      </span>
      <span className="dsg-rsp-check-text">
        <span className={checked ? 'dsg-rsp-check-label is-on' : 'dsg-rsp-check-label'}>
          {label}
        </span>
        {sub && <span className="dsg-rsp-check-sub">{sub}</span>}
      </span>
    </button>
  );
}

function Swatch({
  css,
  label,
  active,
  onClick,
}: {
  css: string | null;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`dsg-rsp-swatch${active ? ' is-active' : ''}`}
      aria-label={label}
      aria-pressed={active}
      title={label}
      onClick={onClick}
    >
      {/* The 32px button is the hit area (DESIGN §4); the span is the color. */}
      <span
        className="dsg-rsp-swatch-color"
        style={css ? { background: css } : undefined}
        aria-hidden="true"
      >
        {!css && <span className="dsg-rsp-swatch-none" />}
      </span>
    </button>
  );
}

function MarkStyleControls({
  value,
  onChange,
}: {
  value: RevisionMarkStyle;
  onChange: (patch: Partial<RevisionMarkStyle>) => void;
}) {
  const chip = (active: boolean, label: string, onClick: () => void, pressed?: boolean) => (
    <button
      type="button"
      key={label}
      className={`dsg-rsp-chip${active ? ' is-active' : ''}`}
      aria-pressed={pressed ?? active}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    <div className="dsg-rsp-controls">
      {/* Live preview on paper-white so highlight colors read true in both
          themes — this swatch previews PRINT, not UI. markCssText is the same
          function the plugin decorates the paper with, so the preview cannot
          drift from the real marks. */}
      <div className="dsg-rsp-preview">
        <span
          ref={(el) => {
            if (el) el.style.cssText = 'color:#1a1a1a;' + markCssText(value);
          }}
        >
          Concrete shall attain 4,000 psi
        </span>
      </div>
      <div className="dsg-rsp-chip-row">
        {chip(!!value.strikethrough, 'Strike', () =>
          onChange({ strikethrough: !value.strikethrough })
        )}
        {chip(!!value.bold, 'Bold', () => onChange({ bold: !value.bold }))}
        {(['none', 'single', 'double'] as const).map((u) =>
          chip(
            (value.underline || 'none') === u,
            u === 'none' ? 'No line' : u === 'single' ? 'Underline' : 'Dbl underline',
            () => onChange({ underline: u })
          )
        )}
      </div>
      <div className="dsg-rsp-swatch-row">
        <span className="dsg-rsp-swatch-label">Highlight</span>
        {HIGHLIGHTS.map((h) => (
          <Swatch
            key={h.key}
            css={h.css}
            label={h.label}
            active={(value.highlight || 'none') === h.key}
            onClick={() => onChange({ highlight: h.key })}
          />
        ))}
      </div>
      <div className="dsg-rsp-swatch-row">
        <span className="dsg-rsp-swatch-label">Color</span>
        <Swatch
          css={null}
          label="Automatic (body color)"
          active={!value.color}
          onClick={() => onChange({ color: undefined })}
        />
        <ColorPickerPopover
          value={'#' + (value.color || '1A1A1A')}
          onChange={(hex) => onChange({ color: hex.replace('#', '') })}
          swatches={COLORS.map((c) => c.css).filter((css): css is string => !!css)}
          allowAlpha={false}
          aria-label="Text color"
        />
      </div>
    </div>
  );
}

export interface RevisionStylesPanelProps {
  /** Anchor rect of the trigger button (getBoundingClientRect at open). */
  anchor: { left: number; bottom: number; right: number };
  settings: RevisionSettings;
  baseline: Issuance | null;
  /** The manual issuance name shown in the "marking since" line, if any. */
  baselineLabel?: string;
  canEdit: boolean;
  onChange: (next: RevisionSettings) => void;
  onClose: () => void;
}

/** Portalled, fixed-position, viewport-clamped; dismisses on Esc and on
 *  mousedown outside (never mouseup — a drag out survives). */
export function RevisionStylesPanel({
  anchor,
  settings,
  baseline,
  baselineLabel,
  canEdit,
  onChange,
  onClose,
}: RevisionStylesPanelProps) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  React.useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, []);

  const setMark = (key: 'added' | 'deleted', patch: Partial<RevisionMarkStyle>) =>
    onChange({ ...settings, [key]: { ...settings[key], ...patch } });

  const left = Math.max(8, Math.min(anchor.right - PANEL_W, window.innerWidth - PANEL_W - 8));
  const maxH = Math.min(560, window.innerHeight - anchor.bottom - 16);
  const sinceLabel = baselineLabel || baseline?.name;

  return createPortal(
    <div
      {...appLayerProps}
      ref={ref}
      className="dsg-menu dsg-rsp"
      role="menu"
      aria-label="Revision mark settings"
      style={{ left, top: anchor.bottom + 6, width: PANEL_W, maxHeight: maxH }}
    >
      <div className="dsg-rsp-baseline">
        {baseline
          ? `Marking changes since “${sinceLabel}” (${(baseline.date || baseline.createdAt || '').slice(0, 10)})`
          : 'No issuance yet — issue the manual to start tracking changes.'}
      </div>
      <fieldset className="dsg-rsp-fieldset" disabled={!canEdit}>
        <CheckRow
          label="Show revision marks"
          sub="Render change marks on the paper"
          checked={settings.showMarks !== false}
          onToggle={() => onChange({ ...settings, showMarks: !(settings.showMarks !== false) })}
        />
        <CheckRow
          label="Change bars"
          sub="Vertical line in the margin beside changed paragraphs"
          checked={settings.changeBars !== false}
          onToggle={() => onChange({ ...settings, changeBars: !(settings.changeBars !== false) })}
        />
        <div className="dsg-menu-sep" role="separator" />
        <div className="dsg-rsp-section">New text</div>
        <MarkStyleControls value={settings.added} onChange={(p) => setMark('added', p)} />
        <div className="dsg-menu-sep" role="separator" />
        <div className="dsg-rsp-section">Deleted text</div>
        <MarkStyleControls value={settings.deleted} onChange={(p) => setMark('deleted', p)} />
      </fieldset>
    </div>,
    document.body
  );
}
